import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getOrders, type OrderListItem } from '../../shared/api/orders.js'
import { getLocations } from '../../shared/api/locations.js'
import type { Location } from '../../shared/types/location.js'
import { ApiError } from '../../shared/types/api.js'
import { ROUTES } from '../../app/routes.js'
import { ORDER_STATUSES } from '../../shared/utils/orders.js'
import OrderStatusBadge from './components/OrderStatusBadge.js'
import OrdersKanban from './components/OrdersKanban.js'
import EmptyState from '../menu/components/EmptyState.js'
import { SkeletonOrderRow } from '../../shared/ui/Skeleton.js'

type ListStatus = 'loading' | 'ready' | 'error' | 'forbidden'
type ViewMode = 'kanban' | 'list'

const PAGE_SIZE = 20

const TYPE_LABEL: Record<string, string> = {
  DELIVERY: 'orderType.delivery',
  PICKUP: 'orderType.pickup',
  DINE_IN: 'orderType.dineIn',
}

export default function OrdersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role

  const [view, setView] = useState<ViewMode>('kanban')
  const [orders, setOrders] = useState<OrderListItem[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 })

  const [statusFilter, setStatusFilter] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<ListStatus>('loading')

  useEffect(() => {
    if (!orgId) return
    getLocations(orgId)
      .then(res => setLocations(res.data))
      .catch(() => setLocations([]))
  }, [orgId])

  const load = useCallback(async () => {
    if (!orgId) return
    setStatus('loading')
    try {
      const res = await getOrders(orgId, {
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
        locationId: locationFilter || undefined,
        type: typeFilter || undefined,
        dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
        dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
      })
      setOrders(res.data)
      setMeta(res.meta)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error')
    }
  }, [orgId, page, statusFilter, locationFilter, typeFilter, dateFrom, dateTo])

  useEffect(() => {
    if (view === 'list') void load()
  }, [view, load])

  const hasActiveFilters = Boolean(statusFilter || locationFilter || typeFilter || dateFrom || dateTo)

  function resetFilters() {
    setStatusFilter('')
    setLocationFilter('')
    setTypeFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  function handleFilterChange(setter: (v: string) => void, value: string) {
    setter(value)
    setPage(1)
  }

  if (!orgId) return null

  return (
    <section className="page-section page-section--wide orders-page">
      <div className="page-header orders-page-header">
        <div>
          <h2>{t('app.orders')}</h2>
          <p className="page-section-description">{t('orders.listSubtitle')}</p>
        </div>
        <div className="view-toggle" role="group" aria-label={t('orders.viewToggle')}>
          <button
            type="button"
            className={view === 'kanban' ? 'view-toggle-button view-toggle-button--active' : 'view-toggle-button'}
            aria-pressed={view === 'kanban'}
            onClick={() => setView('kanban')}
          >
            {t('orders.viewKanban')}
          </button>
          <button
            type="button"
            className={view === 'list' ? 'view-toggle-button view-toggle-button--active' : 'view-toggle-button'}
            aria-pressed={view === 'list'}
            onClick={() => setView('list')}
          >
            {t('orders.viewList')}
          </button>
        </div>
      </div>

      {view === 'kanban' ? (
        <OrdersKanban
          organizationId={orgId}
          role={role}
          locations={locations}
          locationFilter={locationFilter}
          typeFilter={typeFilter}
          onLocationChange={value => handleFilterChange(setLocationFilter, value)}
          onTypeChange={value => handleFilterChange(setTypeFilter, value)}
        />
      ) : (
        <>
          <div className="product-filters">
            <div className="form-field">
              <label htmlFor="orderStatusFilter">{t('orders.statusFilter')}</label>
              <select id="orderStatusFilter" value={statusFilter} onChange={e => handleFilterChange(setStatusFilter, e.target.value)}>
                <option value="">{t('orders.statusAll')}</option>
                {ORDER_STATUSES.map(value => (
                  <option key={value} value={value}>{t(`orderStatus.${value}`)}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="orderLocationFilter">{t('orders.locationFilter')}</label>
              <select id="orderLocationFilter" value={locationFilter} onChange={e => handleFilterChange(setLocationFilter, e.target.value)}>
                <option value="">{t('orders.locationAll')}</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="orderTypeFilter">{t('orders.typeFilter')}</label>
              <select id="orderTypeFilter" value={typeFilter} onChange={e => handleFilterChange(setTypeFilter, e.target.value)}>
                <option value="">{t('orders.typeAll')}</option>
                <option value="DELIVERY">{t('orderType.delivery')}</option>
                <option value="PICKUP">{t('orderType.pickup')}</option>
                <option value="DINE_IN">{t('orderType.dineIn')}</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="orderDateFrom">{t('orders.dateFrom')}</label>
              <input id="orderDateFrom" type="date" value={dateFrom} onChange={e => handleFilterChange(setDateFrom, e.target.value)} />
            </div>

            <div className="form-field">
              <label htmlFor="orderDateTo">{t('orders.dateTo')}</label>
              <input id="orderDateTo" type="date" value={dateTo} onChange={e => handleFilterChange(setDateTo, e.target.value)} />
            </div>
          </div>

          {status === 'loading' && orders.length === 0 && (
            <div className="page-loading">
              {Array.from({ length: 5 }, (_, i) => <SkeletonOrderRow key={i} />)}
            </div>
          )}

          {(status === 'error' || status === 'forbidden') && (
            <div className="settings-card">
              <div className="form-error-banner">
                {status === 'forbidden' ? t('orders.forbidden') : t('orders.loadErrorList')}
              </div>
              <button type="button" className="btn btn-primary" onClick={() => void load()}>
                {t('error.retry')}
              </button>
            </div>
          )}

          {status === 'ready' && orders.length === 0 && (
            hasActiveFilters ? (
              <EmptyState title={t('orders.noResults')} hint={t('orders.noResultsHint')} actionLabel={t('orders.resetFilters')} onAction={resetFilters} />
            ) : (
              <EmptyState title={t('orders.empty')} hint={t('orders.emptyHint')} />
            )
          )}

          {status === 'ready' && orders.length > 0 && (
            <>
              <ul className="order-list">
                {orders.map(order => (
                  <li
                    key={order.id}
                    className="order-row"
                    onClick={() => navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', order.id))}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', order.id))
                    }}
                  >
                    <div className="order-row-main">
                      <span className="order-number">{order.orderNumber}</span>
                      <span className="order-row-type">{t(TYPE_LABEL[order.type] ?? 'orderType.pickup')}</span>
                      <span className="order-row-location">{order.location?.name ?? t('orders.noLocation')}</span>
                    </div>
                    <span className="order-row-customer">{order.guestName || '—'}</span>
                    <div className="order-row-status"><OrderStatusBadge status={order.status} /></div>
                    <div className="order-row-total">{order.total} {order.currency}</div>
                    <div className="order-row-created">{new Date(order.createdAt).toLocaleString()}</div>
                  </li>
                ))}
              </ul>

              {meta.totalPages > 1 && (
                <div className="pagination">
                  <button type="button" className="btn btn-ghost" disabled={meta.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                    {t('orders.previous')}
                  </button>
                  <span className="pagination-label">{t('orders.pageOf', { page: meta.page, total: meta.totalPages })}</span>
                  <button type="button" className="btn btn-ghost" disabled={meta.page >= meta.totalPages} onClick={() => setPage(p => p + 1)}>
                    {t('orders.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}
